import { useCallback, useEffect, useState } from 'react';

import { getMyFeatures } from 'services/api/organizations';

import { AUTH_SESSION_EVENT } from './useOrgBranding';

// Cached so the menu does not flicker its tier-gated items on every reload while
// the request is in flight.
const CACHE_KEY = 'ayro-my-features';

const read = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    if (value) localStorage.setItem(CACHE_KEY, JSON.stringify(value));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* storage unavailable — entitlements simply are not cached */
  }
};

/**
 * The signed-in organization's tier entitlements.
 *
 * Returns null until known. Callers MUST treat null as "not yet known" and show
 * everything: hiding navigation on a pending request would blank the menu on
 * every page load, and a failed lookup should never lock someone out of features
 * they have paid for.
 */
export default function useMyFeatures() {
  const [data, setData] = useState(() => read());

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('serviceToken')) {
      setData(null);
      write(null);
      return;
    }
    try {
      const res = await getMyFeatures();
      setData(res);
      write(res);
    } catch {
      // Leave the previous value in place; see the note above on failing open.
    }
  }, []);

  useEffect(() => {
    refresh();
    const onSession = () => refresh();
    window.addEventListener(AUTH_SESSION_EVENT, onSession);
    window.addEventListener('storage', onSession);
    return () => {
      window.removeEventListener(AUTH_SESSION_EVENT, onSession);
      window.removeEventListener('storage', onSession);
    };
  }, [refresh]);

  return data;
}
